import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Gauge, AlertTriangle, CreditCard, PlayCircle } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { money } from '@/data/constants';
import { LeadBalance, formatPeriodEnd } from '@/lib/leadBalance';
import OnboardingTour from '@/components/OnboardingTour';

interface Props {
  balance: LeadBalance;
  amount?: number;
}

/**
 * The credit balance card, and the permanent home of the REPLAY control for the
 * 5-step buyer walkthrough — the tour can be skipped after a purchase and
 * re-opened from here (the dashboard) forever after.
 */
const LeadBalanceCard: React.FC<Props> = ({ balance, amount }) => {
  const { t, path, lang } = useI18n();
  const [tour, setTour] = useState(false);
  const pct = balance.included > 0 ? Math.min(100, Math.round((balance.used / balance.included) * 100)) : 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-400">
            <Gauge className="h-4 w-4 text-orange-500" /> {t('leads.balanceTitle')}
          </h3>
          {balance.hasSubscription ? (
            <p className="mt-2 text-lg font-extrabold uppercase text-slate-900">
              {balance.planCode}
              {typeof amount === 'number' && amount > 0 && (
                <span className="ml-2 text-sm font-semibold normal-case text-slate-400">
                  {money(amount)}
                  {t('pricing.perMonth')}
                </span>
              )}
            </p>
          ) : (
            <p className="mt-2 text-sm text-slate-600">{t('dash.noSubscription')}</p>
          )}
        </div>
        <div className="text-right">
          <p className="text-4xl font-extrabold leading-none text-slate-900">{balance.remaining}</p>
          <p className="mt-1 text-xs font-bold uppercase tracking-wide text-slate-400">{t('leads.remaining')}</p>
        </div>
      </div>

      {balance.hasSubscription && (
        <>
          <div className="mt-5 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className={`h-full rounded-full transition-all ${balance.exhausted ? 'bg-red-500' : 'bg-emerald-500'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs font-semibold text-slate-500">
            <span>
              {t('leads.usedOf')
                .replace('{used}', String(balance.used))
                .replace('{included}', String(balance.included))}
            </span>
            <span>
              {t('leads.renews')}: {formatPeriodEnd(balance.periodEnd, lang)}
            </span>
          </div>
          {/* Credits expire with the cycle — state it plainly, next to the counter. */}
          <p className="mt-2 text-[11px] font-semibold text-amber-700">{t('leads.noRollover')}</p>
        </>
      )}

      {/*
        Individually purchased $29 credits. They are a separate bucket from the
        plan allowance and are only spent once the cycle allowance is gone
        (the order is enforced server-side inside mkt_claim_lead).
      */}
      {balance.extra > 0 && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
              {t('leads.extraCredits')}
            </span>
            <span className="text-lg font-extrabold text-slate-900">{balance.extra}</span>
          </div>
          <p className="mt-1 text-[11px] leading-relaxed text-slate-500">{t('leads.extraCreditsNote')}</p>
        </div>
      )}

      {balance.exhausted && balance.extra === 0 && (
        <div className="mt-4 flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t('leads.exhausted')}</span>
        </div>
      )}


      {balance.status === 'past_due' && (
        <div className="mt-4 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-800">
          <CreditCard className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{t('leads.pastDue')}</span>
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-3">
        <Link
          to={path('/pricing')}
          className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-slate-800"
        >
          {balance.hasSubscription ? t('leads.upgrade') : t('dash.viewPlans')}
        </Link>
        <Link
          to={path('/jobs')}
          className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
        >
          {t('dash.availableJobs')}
        </Link>
        {/* REPLAY the 5-step buyer walkthrough, any time. */}
        <button
          type="button"
          onClick={() => setTour(true)}
          style={{ touchAction: 'manipulation' }}
          className="inline-flex items-center gap-2 rounded-xl border border-orange-300 bg-orange-50 px-5 py-2.5 text-sm font-bold text-orange-700 hover:bg-orange-100"
        >
          <PlayCircle className="h-4 w-4" /> {t('tour.replay')}
        </button>
      </div>
      <p className="mt-2 text-[11px] font-semibold text-slate-400">{t('tour.replayNote')}</p>

      <p className="mt-4 text-xs leading-relaxed text-slate-400">{t('leads.explain')}</p>

      <OnboardingTour
        open={tour}
        onClose={() => setTour(false)}
        plan={balance.planCode}
        credits={balance.spendable}
        jobsHref={path('/jobs')}
      />
    </div>
  );
};

export default LeadBalanceCard;
